import DateFormat from './DateFormat';
import values from '../values/values';

const FALLBACK = '- -';

const safeToFixed = (value, digits, fallback = FALLBACK) =>
    (value != null && !isNaN(Number(value))) ? Number(value).toFixed(digits) : fallback;

/**
 * Distância total em metros a partir de um histórico de coordenadas.
 */
export const calculateDistanceFromCoords = (coords) => {
    if (!coords || !Array.isArray(coords) || coords.length < 2) {
        return 0;
    }

    const validCoords = coords
        .map((item) => {
            const lat = item.c_lat != null ? item.c_lat : (item.lat != null ? item.lat : null);
            const lng = item.c_lng != null ? item.c_lng : (item.lng != null ? item.lng : null);
            const alt = item.c_alt != null ? item.c_alt : (item.alt != null ? item.alt : null);

            if (lat != null && lng != null) {
                return {
                    lat: parseFloat(lat),
                    lng: parseFloat(lng),
                    alt: alt != null ? parseFloat(alt) : undefined,
                };
            }
            return null;
        })
        .filter((item) => item !== null);

    if (validCoords.length < 2) {
        return 0;
    }

    let totalDistance = 0;
    for (let i = 1; i < validCoords.length; i++) {
        const prev = validCoords[i - 1];
        const curr = validCoords[i];
        const distance = parseFloat(values.distanceBetweenpointsM(
            prev.lat,
            prev.lng,
            prev.alt,
            curr.lat,
            curr.lng,
            curr.alt
        ));

        if (typeof distance === 'number' && !isNaN(distance) && isFinite(distance) && distance >= 0) {
            totalDistance += distance;
        }
    }

    return typeof totalDistance === 'number' && !isNaN(totalDistance) && isFinite(totalDistance)
        ? totalDistance
        : 0;
};

const formatHorasTrabalhadas = (trabalho) => {
    if (!trabalho) {
        return FALLBACK;
    }

    let milliseconds = null;
    if (trabalho.plantio?.plantio_ponto?.millsec_trabalhados != null) {
        milliseconds = trabalho.plantio.plantio_ponto.millsec_trabalhados;
    }
    if (trabalho.pulv?.pulv_ponto?.millsec_trabalhados != null) {
        milliseconds = trabalho.pulv.pulv_ponto.millsec_trabalhados;
    }
    if (trabalho.gps?.gps_ponto) {
        const ms = trabalho.gps.gps_ponto.millsec_trabalhados;
        if (ms != null && Number(ms) > 0) {
            milliseconds = ms;
        } else if (trabalho.data_inicio) {
            const fim = trabalho.data_fim || trabalho.gps.gps_ponto.data_cad_device;
            if (fim) {
                const ini = new Date(trabalho.data_inicio).getTime();
                const f = new Date(fim).getTime();
                if (!isNaN(ini) && !isNaN(f)) {
                    milliseconds = Math.max(0, f - ini);
                }
            }
        }
    }

    if (milliseconds == null) {
        return FALLBACK;
    }

    let horas = Number(milliseconds) / 1000 / 60 / 60;
    let minutos = (horas - Math.floor(horas)) * 60;
    return `${Math.floor(horas)}h${Math.floor(minutos)}min`;
};

const formatDistanciaPercorrida = (trabalho, histCoords) => {
    if (histCoords && Array.isArray(histCoords) && histCoords.length >= 2) {
        const distanceFromCoords = calculateDistanceFromCoords(histCoords);
        if (
            typeof distanceFromCoords === 'number' &&
            !isNaN(distanceFromCoords) &&
            isFinite(distanceFromCoords) &&
            distanceFromCoords > 0
        ) {
            return `${distanceFromCoords.toFixed(2)} m`;
        }
    }

    if (trabalho?.plantio?.plantio_ponto?.metros_percorridos != null) {
        return `${safeToFixed(trabalho.plantio.plantio_ponto.metros_percorridos, 2, '0.00')} m`;
    }
    if (trabalho?.pulv?.pulv_ponto?.metros_percorridos != null) {
        return `${safeToFixed(trabalho.pulv.pulv_ponto.metros_percorridos, 2, '0.00')} m`;
    }
    if (trabalho?.gps?.gps_ponto?.metros_percorridos != null) {
        return `${safeToFixed(trabalho.gps.gps_ponto.metros_percorridos, 2, '0.00')} m`;
    }

    return '0.00 m';
};

/**
 * Monta o objeto de dados gerais usado no cabeçalho de impressão de um trabalho.
 */
export const buildTrabalhoPrintInfo = (trabalho, histCoords = null) => {
    if (!trabalho) {
        return null;
    }

    return {
        id: trabalho.id,
        dataInicial: trabalho.data_inicio ? DateFormat(new Date(trabalho.data_inicio)) : FALLBACK,
        dataFinal: trabalho.data_fim ? DateFormat(new Date(trabalho.data_fim)) : FALLBACK,
        horasTrabalhadas: formatHorasTrabalhadas(trabalho),
        distanciaPercorrida: formatDistanciaPercorrida(trabalho, histCoords),
        operador: trabalho.operador || FALLBACK,
        talhao: trabalho.talhao || FALLBACK,
    };
};

const getMillsecTrabalhados = (trabalho) => {
    if (!trabalho) {
        return 0;
    }

    let milliseconds = null;
    if (trabalho.plantio?.plantio_ponto?.millsec_trabalhados != null) {
        milliseconds = trabalho.plantio.plantio_ponto.millsec_trabalhados;
    }
    if (trabalho.pulv?.pulv_ponto?.millsec_trabalhados != null) {
        milliseconds = trabalho.pulv.pulv_ponto.millsec_trabalhados;
    }
    if (trabalho.gps?.gps_ponto) {
        const ms = trabalho.gps.gps_ponto.millsec_trabalhados;
        if (ms != null && Number(ms) > 0) {
            milliseconds = ms;
        } else if (trabalho.data_inicio) {
            const fim = trabalho.data_fim || trabalho.gps.gps_ponto.data_cad_device;
            if (fim) {
                const ini = new Date(trabalho.data_inicio).getTime();
                const f = new Date(fim).getTime();
                if (!isNaN(ini) && !isNaN(f)) {
                    milliseconds = Math.max(0, f - ini);
                }
            }
        }
    }

    return milliseconds != null && !isNaN(Number(milliseconds)) ? Number(milliseconds) : 0;
};

const formatMillsecAsHoras = (milliseconds) => {
    if (milliseconds == null || isNaN(Number(milliseconds)) || Number(milliseconds) < 0) {
        return FALLBACK;
    }
    const horas = Number(milliseconds) / 1000 / 60 / 60;
    const minutos = (horas - Math.floor(horas)) * 60;
    return `${Math.floor(horas)}h${Math.floor(minutos)}min`;
};

const getMetrosPercorridos = (trabalho, histCoords = null) => {
    if (histCoords && Array.isArray(histCoords) && histCoords.length >= 2) {
        const distanceFromCoords = calculateDistanceFromCoords(histCoords);
        if (
            typeof distanceFromCoords === 'number' &&
            !isNaN(distanceFromCoords) &&
            isFinite(distanceFromCoords) &&
            distanceFromCoords > 0
        ) {
            return distanceFromCoords;
        }
    }

    if (trabalho?.plantio?.plantio_ponto?.metros_percorridos != null) {
        return Number(trabalho.plantio.plantio_ponto.metros_percorridos) || 0;
    }
    if (trabalho?.pulv?.pulv_ponto?.metros_percorridos != null) {
        return Number(trabalho.pulv.pulv_ponto.metros_percorridos) || 0;
    }
    if (trabalho?.gps?.gps_ponto?.metros_percorridos != null) {
        return Number(trabalho.gps.gps_ponto.metros_percorridos) || 0;
    }

    return 0;
};

const joinUniqueNonEmpty = (values) => {
    const seen = new Set();
    const result = [];
    for (const value of values) {
        const trimmed = value != null ? String(value).trim() : '';
        if (!trimmed || seen.has(trimmed)) {
            continue;
        }
        seen.add(trimmed);
        result.push(trimmed);
    }
    return result.length > 0 ? result.join(', ') : FALLBACK;
};

/**
 * Agrega dados gerais de múltiplos trabalhos para o card "Resumo dos trabalhos".
 * entries: [{ trabalho, histCoords }]
 */
export const buildTrabalhosResumoInfo = (entries = []) => {
    const validEntries = (Array.isArray(entries) ? entries : []).filter((entry) => entry?.trabalho);
    if (validEntries.length < 2) {
        return null;
    }

    let minInicio = null;
    let maxFim = null;
    let totalMillsec = 0;
    let totalMetros = 0;
    const operadores = [];
    const talhoes = [];

    for (const entry of validEntries) {
        const { trabalho, histCoords } = entry;

        if (trabalho.data_inicio) {
            const inicio = new Date(trabalho.data_inicio).getTime();
            if (!isNaN(inicio) && (minInicio == null || inicio < minInicio)) {
                minInicio = inicio;
            }
        }

        if (trabalho.data_fim) {
            const fim = new Date(trabalho.data_fim).getTime();
            if (!isNaN(fim) && (maxFim == null || fim > maxFim)) {
                maxFim = fim;
            }
        }

        totalMillsec += getMillsecTrabalhados(trabalho);
        totalMetros += getMetrosPercorridos(trabalho, histCoords);
        operadores.push(trabalho.operador);
        talhoes.push(trabalho.talhao);
    }

    return {
        dataInicial: minInicio != null ? DateFormat(new Date(minInicio)) : FALLBACK,
        dataFinal: maxFim != null ? DateFormat(new Date(maxFim)) : FALLBACK,
        horasTrabalhadas: formatMillsecAsHoras(totalMillsec),
        distanciaPercorrida: `${Number(totalMetros).toFixed(2)} m`,
        operador: joinUniqueNonEmpty(operadores),
        talhao: joinUniqueNonEmpty(talhoes),
    };
};
